"use client";

import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { formatIsoDate, formatUSPhoneNumber } from "@/src/lib/utils/date-and-phone-utils";
import { Referral } from "@/src/models/documents/referral";
import { getFullAddress, getPatientName, getPatientPhoneNumber } from "@/src/models/patient";
import { getPersonName } from "@/src/models/person";

// Mirrors the ModMed "Communication" task Apex creates via CloudCruise
// (configuration/groups/apex/workflow-steps.ts buildPatientTaskCreateInputs).
const APEX_TASK_TYPE = "Communication";
const APEX_ASSIGNEE = "New Referrals";

interface ApexReferralInstructionsProps {
  referral: Referral;
  taskDetails: string;
  note: string;
}

export function ApexReferralInstructions({ referral, taskDetails, note }: ApexReferralInstructionsProps) {
  const referringProviderName = referral.referringProvider ? getPersonName(referral.referringProvider) : "";
  const referringProviderNpi = referral.referringProvider?.npiNumber ?? null;

  const patient = referral.patient;
  const patientName = getPatientName(patient);
  const patientDob = patient.dateOfBirth ? formatIsoDate(patient.dateOfBirth) : null;
  const patientPhoneRaw = getPatientPhoneNumber(patient);
  const patientPhone = patientPhoneRaw ? formatUSPhoneNumber(patientPhoneRaw) : null;
  const patientAddress = getFullAddress(patient);

  return (
    <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">
      <EhrInstructionsCard>
        <div className="flex flex-col gap-4 text-sm">
          <span className="font-medium text-center">Apex Referral Instructions:</span>
          <p className="text-muted-foreground">
            Reference for the ModMed task created for this referral. Click any value to copy.
          </p>

          <div className="space-y-3">
            <div>
              <span className="font-semibold text-foreground">Referring Provider</span>
              <div className="ml-4 mt-1 flex flex-col gap-1">
                <div>
                  <span className="text-muted-foreground">Name: </span>
                  {referringProviderName ? (
                    <CopyableContent text={referringProviderName} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">NPI: </span>
                  {referringProviderNpi ? (
                    <CopyableContent text={referringProviderNpi} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
              </div>
            </div>

            <div>
              <span className="font-semibold text-foreground">Patient Demographics</span>
              <div className="ml-4 mt-1 flex flex-col gap-1">
                <div>
                  <span className="text-muted-foreground">Name: </span>
                  {patientName ? (
                    <CopyableContent text={patientName} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Date of Birth: </span>
                  {patientDob ? (
                    <CopyableContent text={patientDob} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Phone: </span>
                  {patientPhone ? (
                    <CopyableContent text={patientPhone} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Address: </span>
                  {patientAddress ? (
                    <CopyableContent text={patientAddress} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
              </div>
            </div>

            <div>
              <span className="text-muted-foreground">Task: </span>
              <CopyableContent text={APEX_TASK_TYPE} />
            </div>
            <div>
              <span className="text-muted-foreground">Assign To: </span>
              <CopyableContent text={APEX_ASSIGNEE} />
            </div>
            <div>
              <span className="text-muted-foreground">Task Details: </span>
              {taskDetails ? (
                <CopyableContent text={taskDetails} />
              ) : (
                <span className="text-muted-foreground italic">Not provided</span>
              )}
            </div>
            <div>
              <span className="text-muted-foreground">Note: </span>
              {note ? (
                <CopyableContent text={note} />
              ) : (
                <span className="text-muted-foreground italic">Not provided</span>
              )}
            </div>
          </div>
        </div>
      </EhrInstructionsCard>

      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable />}
      {referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable />}
    </div>
  );
}
